import { getToken } from "firebase/messaging";
import { FIREBASE_CONFIG, FIREBASE_VAPID_KEY } from "configs/AppConfig";
import {
  deletePushToken,
  savePushToken,
} from "modules/notifications/api/pushTokens";
import { getFcmMessaging } from "services/firebase";

// Where the user's own on/off choice lives, and how the parts of the app that
// change it tell useFcmToken to act on it.
export const PUSH_SETTING_KEY = "push_notifications_enabled";
export const PUSH_SETTING_EVENT = "push-notifications-setting-changed";

const STORED_TOKEN_KEY = "fcm_token";

export const isPushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

/** @returns {"unsupported"|"default"|"granted"|"denied"} */
export const pushPermission = () =>
  isPushSupported() ? Notification.permission : "unsupported";

export const isPushEnabledInSettings = () =>
  localStorage.getItem(PUSH_SETTING_KEY) !== "false";

export const setPushEnabledInSettings = (enabled) => {
  localStorage.setItem(PUSH_SETTING_KEY, enabled ? "true" : "false");
  window.dispatchEvent(new Event(PUSH_SETTING_EVENT));
};

/**
 * Firefox and Safari only show the prompt when this call carries transient user
 * activation. A mount-time effect has none, and an `await` before the call
 * spends it — so call this FIRST inside a click handler. Everything the token
 * needs afterwards is activation-free and can await freely.
 */
export const requestPushPermission = () => Notification.requestPermission();

/**
 * `register()` resolves as soon as the registration object exists — the worker
 * inside it may still be installing, and `getToken()` subscribes through that
 * registration's PushManager, which throws AbortError "no active Service Worker"
 * until one activates.
 */
const waitForActiveWorker = (registration) => {
  if (registration.active) return Promise.resolve(registration);

  const worker = registration.installing || registration.waiting;

  // Nothing in flight and nothing active: let the browser say when a
  // registration for this scope is ready.
  if (!worker) return navigator.serviceWorker.ready;

  return new Promise((resolve, reject) => {
    const settle = () => {
      if (worker.state === "activated") {
        worker.removeEventListener("statechange", settle);
        resolve(registration);
      } else if (worker.state === "redundant") {
        worker.removeEventListener("statechange", settle);
        reject(
          new Error("Push service worker went redundant before activating.")
        );
      }
    };

    worker.addEventListener("statechange", settle);
    // It may have activated between the check above and the listener going on.
    settle();
  });
};

// The worker is a static file, so the Firebase config reaches it as a query
// string rather than through the bundler.
const serviceWorkerUrl = () => {
  const params = new URLSearchParams({
    apiKey: FIREBASE_CONFIG.apiKey || "",
    authDomain: FIREBASE_CONFIG.authDomain || "",
    projectId: FIREBASE_CONFIG.projectId || "",
    storageBucket: FIREBASE_CONFIG.storageBucket || "",
    messagingSenderId: FIREBASE_CONFIG.messagingSenderId || "",
    appId: FIREBASE_CONFIG.appId || "",
    measurementId: FIREBASE_CONFIG.measurementId || "",
  });

  return `/firebase-messaging-sw.js?${params.toString()}`;
};

/**
 * Register this browser for push and hand the token to the backend. Requires
 * permission to have been granted already — asking for it is the caller's job,
 * because only the caller can do it from a user gesture.
 *
 * @returns {Promise<string|null>} the registration token, or null if none was issued
 */
export const registerPushToken = async () => {
  if (pushPermission() !== "granted") return null;

  if (!FIREBASE_VAPID_KEY || FIREBASE_VAPID_KEY === "YOUR_VAPID_KEY_HERE") {
    console.warn("Firebase VAPID key is missing or not configured.");
    return null;
  }

  const messaging = await getFcmMessaging();
  if (!messaging) {
    console.warn("FCM messaging instance could not be initialized.");
    return null;
  }

  const registration =
    await navigator.serviceWorker.register(serviceWorkerUrl());
  await waitForActiveWorker(registration);

  const token = await getToken(messaging, {
    vapidKey: FIREBASE_VAPID_KEY,
    serviceWorkerRegistration: registration,
  });

  if (!token) {
    console.warn("No registration token available.");
    return null;
  }

  // Only when it changed — the backend row is keyed on the token itself.
  if (localStorage.getItem(STORED_TOKEN_KEY) !== token) {
    await savePushToken(token);
    localStorage.setItem(STORED_TOKEN_KEY, token);
  }

  return token;
};

/** Unregister the worker and drop the row this browser owns. */
export const unregisterPushToken = async () => {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      if (registration.active?.scriptURL.includes("firebase-messaging-sw.js")) {
        await registration.unregister();
      }
    }

    // Named, because the backend cannot tell which of the user's devices
    // unsubscribed from anything else.
    const savedToken = localStorage.getItem(STORED_TOKEN_KEY);
    if (savedToken) {
      await deletePushToken(savedToken).catch((error) =>
        console.error("Error clearing push token on backend:", error)
      );
      localStorage.removeItem(STORED_TOKEN_KEY);
    }
  } catch (error) {
    console.error("Error during push cleanup:", error);
  }
};
